import { useEffect, useMemo, useState } from 'react';

import { renderRouteOptions, Routes } from '@cms/utils/routes';
import { useAppSelector } from '@cms/utils/store/hooks';
import { ControlledSystemMultiTab } from '../../components/data/MultiTab';
import PageContainer from '../../components/ui/PageContainer';

export const GenerateEmailSignatureTool = () => {
  const { profile: userProfile } = useAppSelector((state) => state.user);

  const [formData, setFormData] = useState<{
    name: string;
    title: string;
    number: string;
    linkedinLink: string;
    telegramLink: string;
    xLink: string;
    type: "dlnews" | "dlresearch";
  }>({
    name: '',
    title: '',
    number: '',
    linkedinLink: '',
    telegramLink: '',
    xLink: '',
    type: 'dlnews',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Logic to generate email signature
    const signatureElement = document.getElementById('generate-email-signature');
    if (signatureElement) {
      const range = document.createRange();
      range.selectNode(signatureElement);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        alert('Email signature copied to clipboard. You can now paste it in the Gmail signature settings.');
      }
    }
  };

  const renderEmailSignature = ({
    name,
    title,
    number,
    linkedinLink,
    telegramLink,
    xLink,
    type = 'dlnews',
  }: {
    name: string;
    title: string;
    number: string;
    linkedinLink: string;
    telegramLink: string;
    xLink: string;
    type: string;
  }) => (
    <div
      dangerouslySetInnerHTML={{
        __html: `
     <table id="generate-email-signature" style="width: 100%; max-height:130px; max-width: 500px; font-family: Arial, sans-serif; color: #0c2956;" cellpadding="0" cellspacing="0" border="0">
          <tbody>
            <tr>
              <td style="width:15px; background-color:#0c2956;">
                <img 
                  src="https://dl-fixed-assets.s3.eu-central-1.amazonaws.com/GENERATE_SIGNATURE_1.jpeg" 
                  alt="DL News Logo" 
                  style="width: 100%; height: 130px; object-fit: cover; display: block; border: none;" 
                  />
              </td>
              
              <td style="padding: 0px 10px;">
                <p style="margin: 0; font-size: 14px; font-weight: bold;">${name}</p>
                <p style="margin: 0 0 10px 0; font-size: 12px;">${title}</p>
                
                <p style="margin: 0; font-size: 12px;">
                  ${number ? `<a href="tel:${number}" style="font-weight: bold;">${number}</a><br>` : ``}
                  ${telegramLink ? `<a href="${telegramLink}" style="text-decoration:underline;">Telegram</a> |` : ``}
                  ${linkedinLink ? `<a href="${linkedinLink}" style="text-decoration:underline;">Linkedin</a> |` : ``}
                  ${xLink ? `<a href="${xLink}" style="text-decoration:underline;">X</a>` : ``}
                </p>
              </td>

              <td style="width: 256px; padding: 0; margin: 0;">
                <a href="${{
            dlnews: 'https://www.dlnews.com/',
            dlresearch: 'https://www.dlnews.com/research/',
          }[type]}" target="_blank" style="display: block; width: 100%;">
                  <img src="${{
            dlnews:
              'https://dl-fixed-assets.s3.eu-central-1.amazonaws.com/GENERATE_SIGNATURE_2.jpeg',
            dlresearch:
              'https://dl-fixed-assets.s3.eu-central-1.amazonaws.com/GENERATE_SIGNATURE_3.jpg',
          }[type]
          }" 
                      alt="DL News Logo" 
                      style="width: 100%; height: 130px; object-fit: cover; display: block; border: none;" />
                </a>
              </td>
            </tr>

            <tr>
              <td colspan="3">
                <p style="margin: 10px 0 0; font-size: 12px; font-style: italic;">
                  <a href="https://dlnews.com/newsletters" style="text-decoration:underline; color: #1a1a1a;">Subscribe to our newsletters</a>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
    `,
      }}
    ></div>
  );

  useEffect(() => {
    if (userProfile) {
      setFormData((prev) => ({
        ...prev,
        name: userProfile.name || '',
        title: userProfile.title || '',
        xLink: userProfile.social_links?.twitter || '',
        linkedinLink: userProfile.social_links?.linkedin || '',
        telegramLink: userProfile.social_links?.telegram || '',
      }));
    }
  }, [userProfile]);

  const memoizedRenderEmailSignature = useMemo(() => {
    return renderEmailSignature(formData);
  }, [formData]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <div className="text-center p-6 bg-white rounded-lg shadow w-full max-w-md">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Generate Email Signature</h1>
        <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
          <div>
            <label htmlFor='name' className="block text-left text-gray-700">Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>
          <div>
            <label htmlFor='title' className="block text-left text-gray-700">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>
          <div>
            <label htmlFor='number' className="block text-left text-gray-700">Phone Number</label>
            <input
              type="text"
              name="number"
              value={formData.number}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label htmlFor='telegramLink' className="block text-left text-gray-700">Telegram Link</label>
            <input
              type="text"
              name="telegramLink"
              value={formData.telegramLink}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label htmlFor='linkedinLink' className="block text-left text-gray-700">LinkedIn Link</label>
            <input
              type="text"
              name="linkedinLink"
              value={formData.linkedinLink}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label htmlFor='xLink' className="block text-left text-gray-700">X Link</label>
            <input
              type="text"
              name="xLink"
              value={formData.xLink}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label htmlFor='type' className="block text-left text-gray-700">Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="dlnews">DL News</option>
              <option value="dlresearch">DL Research</option>
            </select>
          </div>
          <button
            type="submit"
            className="w-full p-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Generate Signature
          </button>
        </form>
      </div>
      <div className="mt-6 w-full max-w-md">
        {memoizedRenderEmailSignature}
      </div>
    </div>
  );
};

const TABS = [
  {
    label: 'Generate Email Signature',
    content: (
      <GenerateEmailSignatureTool />
    ),
  }
];

const ToolsPage = () => {
  return (
    <PageContainer
      title={renderRouteOptions(Routes.TOOLS).title}
    >
      <ControlledSystemMultiTab
        tabs={TABS}
      />
    </PageContainer>
  )
}

export default ToolsPage